// Reusable SEO head injector — title, description, canonical, Open Graph, JSON-LD schema.
import { Helmet } from "react-helmet-async";

const SITE_NAME = "BestRx";
const SITE_URL = "https://bestrx.online";
const DEFAULT_DESCRIPTION =
  "BestRx.online — provider-guided weight loss, hormones, performance and longevity programs. Licensed clinicians, FDA-regulated pharmacies, real outcomes.";
const DEFAULT_OG_IMAGE = `${SITE_URL}/assets/bestrx-logo.png`;

export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  schema,
  noindex = false,
  lang = "en",                  // current page language (en | es)
  langAlt = null,               // { en: "/path", es: "/es/path" } for hreflang
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} · Provider-Guided Wellness`;
  const canonical = `${SITE_URL}${path}`;
  const schemaArr = Array.isArray(schema) ? schema : schema ? [schema] : [];

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex ? <meta name="robots" content="noindex,nofollow" /> : <meta name="robots" content="index,follow" />}

      {/* hreflang alternates (EN ↔ ES) */}
      {langAlt && langAlt.en && (
        <link rel="alternate" hrefLang="en" href={`${SITE_URL}${langAlt.en}`} />
      )}
      {langAlt && langAlt.es && (
        <link rel="alternate" hrefLang="es" href={`${SITE_URL}${langAlt.es}`} />
      )}
      {langAlt && langAlt.en && (
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${langAlt.en}`} />
      )}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={lang === "es" ? "es_US" : "en_US"} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD */}
      {schemaArr.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}

// Helper schema builders
export const orgSchema = () => ({
  "@context": "https://schema.org",
  "@type": "MedicalBusiness",
  name: SITE_NAME,
  url: SITE_URL,
  logo: DEFAULT_OG_IMAGE,
  telephone: "+1-214-815-5966",
  email: "care@bestrx.online",
  address: {
    "@type": "PostalAddress",
    streetAddress: "400 N Ervay St Unit 130185",
    addressLocality: "Dallas",
    addressRegion: "TX",
    postalCode: "75313",
    addressCountry: "US",
  },
  medicalSpecialty: ["Endocrine", "Weight Loss", "Hormones", "Sexual Health", "Dermatology"],
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export const drugSchema = (med) => ({
  "@context": "https://schema.org",
  "@type": "Drug",
  name: med.name,
  description: med.summary || med.tagline || `${med.name} program available through BestRx provider-guided care.`,
  manufacturer: { "@type": "Organization", name: "Licensed U.S. Compounding Pharmacies" },
  prescriptionStatus: "PrescriptionOnly",
});

export const articleSchema = (article, path) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: article.title,
  description: article.summary,
  author: { "@type": "Organization", name: SITE_NAME },
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    logo: { "@type": "ImageObject", url: DEFAULT_OG_IMAGE },
  },
  mainEntityOfPage: `${SITE_URL}${path}`,
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export { SITE_URL, SITE_NAME, DEFAULT_DESCRIPTION };
